import ContactDiscountTerms from "./ContactDiscountTerms";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Spinner,
} from "react-bootstrap";
import api from "../../axiosConfig";

export default function ContacteEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [judetOptions, setJudetOptions] = useState([]);
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    // OBLIGATORII
    termen_plata_zile: "",
    bxdenumire: "",
    bxcodfiscal: "",
    bxadresa: "",
    bxjudet_id: "",
    judet_str: "",
    // OPȚIONALE
    bxoras: "",
    bxcodpostal: "",
    bxtara: "România",
    bxemail: "",
    bxtel: "",
    bxregcom: "",
    bxbanca_nume: "",
    bxbanca_cont: "",
    bxseria: "",
    bxfax: "",
    bxinout_bool: 1,
    bxcatalog_id: "",
    bxsector_id: "",
  });

  // Load judet & catalog options
  useEffect(() => {
    api
      .get("/judet/", { params: { limit: 100 } })
      .then((res) => setJudetOptions(res.data?.items ?? []))
      .catch(() => setJudetOptions([]));
    api
      .get("/catalog/", { params: { limit: 100 } })
      .then((res) => setCatalogOptions(res.data?.items ?? []))
      .catch(() => setCatalogOptions([]));
  }, []);

  // Load contact details
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/contacte/${id}`)
      .then((res) => {
        if (!alive) return;
        const d = res.data || {};
        setForm({
          termen_plata_zile: d.termen_plata_zile ?? "",
          bxdenumire: d.bxdenumire ?? "",
          bxcodfiscal: d.bxcodfiscal ?? "",
          bxadresa: d.bxadresa ?? "",
          bxjudet_id: d.bxjudet_id ? String(d.bxjudet_id) : "",
          judet_str: d.judet_str ?? "",
          bxoras: d.bxoras ?? "",
          bxcodpostal: d.bxcodpostal ?? "",
          bxtara: d.bxtara ?? "România",
          bxemail: d.bxemail ?? "",
          bxtel: d.bxtel ?? "",
          bxregcom: d.bxregcom ?? "",
          bxbanca_nume: d.bxbanca_nume ?? "",
          bxbanca_cont: d.bxbanca_cont ?? "",
          bxseria: d.bxseria ?? "",
          bxfax: d.bxfax ?? "",
          bxinout_bool: d.bxinout_bool ?? 1,
          bxcatalog_id: d.bxcatalog_id ?? "",
          bxsector_id: d.bxsector_id ?? "",
        });
        setLoading(false);
      })
      .catch((err) => {
        alert("Eroare la încărcare contact: " + (err.response?.data?.detail || err.message));
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const handleJudetChange = (e) => {
    const jId = e.target.value;
    const opt = judetOptions.find((o) => String(o.id) === String(jId));
    setForm((f) => ({
      ...f,
      bxjudet_id: jId,
      judet_str: opt?.denumire || "",
    }));
    if (errors.bxjudet_id)
      setErrors((er) => ({ ...er, bxjudet_id: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.bxdenumire.trim()) er.bxdenumire = "Denumirea este obligatorie.";
    if (!form.bxcodfiscal.trim()) er.bxcodfiscal = "CUI este obligatoriu.";
    if (!form.bxadresa.trim()) er.bxadresa = "Adresa este obligatorie.";
    if (!String(form.bxjudet_id)) er.bxjudet_id = "Alege județul.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const toIntOrUndef = (v) => (v === "" || v == null ? undefined : Number(v));

  const cleanPayload = (obj) => {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      if (v !== "" && v != null) out[k] = v;
    }

    // Convert to integers
    ["bxcatalog_id", "bxsector_id", "bxjudet_id"].forEach((k) => {
      if (k in out) out[k] = toIntOrUndef(out[k]);
      if (out[k] === undefined) delete out[k];
    });

    // bxinout_bool must be integer 1 or 2
    out.bxinout_bool = [1, 2].includes(Number(out.bxinout_bool))
      ? Number(out.bxinout_bool)
      : 1;

    // Do not send e_portal if not needed
    delete out.e_portal;

    out.termen_plata_zile = obj.termen_plata_zile === "" ? null : Number(obj.termen_plata_zile);
    return out;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    const payload = cleanPayload(form);

    try {
      await api.put(`/contacte/${id}`, payload);
      navigate("/contacte");
    } catch (err) {
      const detail = err?.response?.data;
      let msg = err?.message || "Eroare necunoscută";

      if (Array.isArray(detail)) {
        msg = detail.map((e) => `${(e.loc || []).join(".")}: ${e.msg}`).join("\n");
      } else if (detail?.detail) {
        msg = typeof detail.detail === "string" ? detail.detail : JSON.stringify(detail.detail);
      }

      alert("Eroare la actualizare:\n" + msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Se încarcă datele contactului...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Card className="p-3">
        <h3 className="mb-3">Editare contact: {form.bxdenumire}</h3>
        <Form onSubmit={handleSubmit} noValidate>
          <Row className="g-3">
            {/* CUI */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>
                  CUI <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="bxcodfiscal"
                  value={form.bxcodfiscal}
                  onChange={handleChange}
                  placeholder="RO1234567 sau 1234567"
                  isInvalid={!!errors.bxcodfiscal}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.bxcodfiscal}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Denumire */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>
                  Denumire <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="bxdenumire"
                  value={form.bxdenumire}
                  onChange={handleChange}
                  isInvalid={!!errors.bxdenumire}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.bxdenumire}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Adresă */}
            <Col xs={12}>
              <Form.Group>
                <Form.Label>
                  Adresă <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="bxadresa"
                  value={form.bxadresa}
                  onChange={handleChange}
                  isInvalid={!!errors.bxadresa}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.bxadresa}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Județ */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>
                  Județ <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="bxjudet_id"
                  value={form.bxjudet_id ? String(form.bxjudet_id) : ""}
                  onChange={handleJudetChange}
                  isInvalid={!!errors.bxjudet_id}
                >
                  <option value="">— alege —</option>
                  {judetOptions.map((opt) => (
                    <option key={opt.id} value={String(opt.id)}>
                      {opt.denumire ?? opt.cod ?? opt.id}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.bxjudet_id}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Oraș */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Oraș</Form.Label>
                <Form.Control
                  name="bxoras"
                  value={form.bxoras}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            {/* Email */}
            <Col xs={12} md={6}>
              <Form.Group controlId="contact-payment-days" className="mb-3"><Form.Label>Termen de plată (zile)</Form.Label><Form.Control type="number" name="termen_plata_zile" min="0" max="3650" step="1" value={form.termen_plata_zile} onChange={handleChange} placeholder="30 implicit"/><Form.Text>Necompletat: 30 de zile. Zero: scadență în ziua facturii.</Form.Text></Form.Group>
              <ContactDiscountTerms contactId={id} />
                <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  name="bxemail"
                  value={form.bxemail}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            {/* Telefon */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Telefon</Form.Label>
                <Form.Control
                  name="bxtel"
                  value={form.bxtel}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            {/* Cod poștal */}
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>Cod poștal</Form.Label>
                <Form.Control
                  name="bxcodpostal"
                  value={form.bxcodpostal}
                  onChange={handleChange}
                  placeholder="ex: 010101"
                />
              </Form.Group>
            </Col>

            {/* Țara */}
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>Țara</Form.Label>
                <Form.Control
                  name="bxtara"
                  value={form.bxtara}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            {/* Reg. Com. */}
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>Reg. Com.</Form.Label>
                <Form.Control
                  name="bxregcom"
                  value={form.bxregcom}
                  onChange={handleChange}
                  placeholder="J00/123/2020"
                />
              </Form.Group>
            </Col>

            {/* Bancă */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Bancă</Form.Label>
                <Form.Control
                  name="bxbanca_nume"
                  value={form.bxbanca_nume}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            {/* IBAN */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>IBAN</Form.Label>
                <Form.Control
                  name="bxbanca_cont"
                  value={form.bxbanca_cont}
                  onChange={handleChange}
                  placeholder="RO..."
                />
              </Form.Group>
            </Col>

            {/* Tip persoană */}
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label>Tip persoană</Form.Label>
                <Form.Select
                  name="bxinout_bool"
                  value={form.bxinout_bool}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      bxinout_bool: Number(e.target.value),
                    }))
                  }
                >
                  <option value={1}>1 — Persoană juridică</option>
                  <option value={2}>2 — Persoană fizică</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Catalog */}
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label>Catalog</Form.Label>
                <Form.Select
                  name="bxcatalog_id"
                  value={form.bxcatalog_id || ""}
                  onChange={handleChange}
                >
                  <option value="">— Fără catalog —</option>
                  {catalogOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.nume_catalog}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Sector ID */}
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label>Sector ID</Form.Label>
                <Form.Control
                  type="number"
                  name="bxsector_id"
                  value={form.bxsector_id}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            {/* Serie CI / Doc */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Serie CI / Doc</Form.Label>
                <Form.Control
                  name="bxseria"
                  value={form.bxseria}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            {/* Fax */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Fax</Form.Label>
                <Form.Control
                  name="bxfax"
                  value={form.bxfax}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            {/* Denumire Județ (read-only) */}
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label>Denumire Județ (judet_str)</Form.Label>
                <Form.Control
                  name="judet_str"
                  value={form.judet_str}
                  disabled
                  placeholder="Auto-completat la alegerea județului"
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-4">
            <Button
              variant="outline-secondary"
              onClick={() => navigate(-1)}
              disabled={submitting}
            >
              Renunță
            </Button>
            <Button type="submit" variant="dark" disabled={submitting}>
              {submitting ? "Se salvează…" : "Salvează"}
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
}
